//------------------------------------------------------------------------------
// BFA10102G 共架申込 基本情報 - 電柱選択
//------------------------------------------------------------------------------
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';

import { doShowMessage } from '@/reducks/common/operations';
import {
  PositiveButton,
  StandardButton,
} from '@/components/atoms/Buttons/PositiveButton';
import DisplayField from '@/components/display-field';
import backend from '@/common/backend';
import PoleSelectMap from './pole-map';
import PoleSelectList from './pole-list';
import PoleSelectForm from './pole-form';

/**
 * 電柱選択ダイアログ
 */
export default function PoleSelectDialog({
  initialPole,
  isDialogOpen,
  onDialogClose,
}) {

  // ステート
  const [ warnings, setWarnings ] = useState([]);      // メッセージ
  const [ poleList, setPoleList ] = useState([]);      // 電柱一覧
  const [ selected, setSelected ] = useState(null);    // 選択した電柱

  // メッセージ表示
  const dispatch = useDispatch();
  const showMessage = (props) => dispatch(doShowMessage(props));

  // 終了処理
  React.useEffect(() => {
    if (!isDialogOpen) {
      setWarnings([]);
      setPoleList([]);
      setSelected(null);
    }
  }, [isDialogOpen]);

  // 検索ボタン処理
  const handleSearch = React.useCallback(async (formValues) => {
    console.info('電柱検索', formValues);

    // 電柱検索
    const res = await backend.post('/master/poles', formValues);
    const poleList = res
        .parse(showMessage)
        .parseBodyAsArray({
          max: 100,             // 最大100件
          error: 'BFY000015W',  // 上限超えメッセージ
        });

    // ステート更新
    setPoleList(poleList);
    setWarnings(res.messages);
  }, []);

  // 電柱一覧選択処理
  const handleListSelect = React.useCallback((selectedPole) => {
    console.info('一覧電柱選択', `select=${selectedPole?.fullText()}`);
    if (selectedPole) {
      setSelected(selectedPole);
    }
  }, []);

  // 電柱地図選択処理
  const handlePoleSelect = React.useCallback((selectedPole) => {
    console.info('地図電柱選択', `select=${selectedPole?.fullText()}`);
    if (selectedPole) {
      setSelected(selectedPole);
    }
  }, []);

  // 電柱選択(決定)ボタン処理
  const handleSelect = React.useCallback(() => {
    console.info('選択電柱決定', `select=${selected?.fullText()}`);
    onDialogClose(selected);
  }, [onDialogClose, selected]);

  // キャンセルボタン処理
  const handleCancel = React.useCallback(() => {
    console.info('電柱選択キャンセル');
    onDialogClose(null);
  }, [onDialogClose]);

  // ダイアログクローズ処理
  const handleClose = React.useCallback((event, reason) => {

    // 領域外クリックとESCキーでは閉じない
    switch (reason) {
      case 'backdropClick': return;
      case 'escapeKeyDown': return;
      default: break;
    }
    onDialogClose(null);
  }, [onDialogClose]);

  // 選択した電柱
  const selectedText = poleDisplayText(selected ?? initialPole);

  //
  // 描画
  //
  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      scroll="paper"
      PaperProps={{
        sx: { height: '95vh' },  // 高さ固定
      }}
    >
      {/* ヘッダー */}
      <DialogTitle sx={{ py: 1 }}>
        電柱選択
      </DialogTitle>
      <Divider sx={{ mb: 1 }}/>

      {/* ボディ */}
      <DialogContent
        sx={{
          m: 0, px: 1, py: 0,
          display: 'flex', flexDirection: 'column',  // 縦に並べる
        }}
      >
        {/* 検索フォーム(上段) */}
        <Box sx={{ px: 1, pb: 1 }}>
          <PoleSelectForm onSearch={handleSearch} />
        </Box> 

        {/* コンテンツ本体(下段) */}
        <Grid container spacing={0}
          columns={{ xs: 1, sm: 1, md: 2 }}
          sx={{
            my: 0, height: '100%',
            flexGrow: 1, minHeight: 0,  // 下段の残り全部
            flexDirection: { xs: 'column', sm: 'column', md: 'row' },
            flexWrap:      { xs: 'nowrap', sm: 'nowrap', md: 'wrap' },
          }}
        >
          {/* 電柱一覧 */}
          <Grid item xs={1} sm={1} md={1}
            sx={{
              m: 0,
              height:    { xs: 'auto', sm: 'auto', md: '100%' },
              maxHeight: { xs: '30vh', sm: '30vh', md: 'none' },
              maxWidth:  { xs: 'none', sm: 'none', md: '28rem' },
            }}
          >
            <PoleSelectList
              selected={selected ?? initialPole}
              poleList={poleList}
              warnings={warnings}
              onSearch={handleSearch}
              onSelect={handleListSelect}
            />

          </Grid>

          {/* 地図表示 */}
          <Grid item xs={1} sm={1} md={1}
            sx={{
              m: 0, p: 1,

              // 小さいサイズ(縦表示)の場合に高さを固定 (ちょっと無理矢理)
              flexGrow: { md: 1 },
              maxWidth: { md: 'none' },
            }}
          >
            <PoleSelectMap
              selected={selected ?? initialPole}
              poleList={poleList}
              onSelect={handlePoleSelect}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* フッター */}
      <DialogActions sx={{ pb: 2, px: 2 }}>

        {/* 選択電柱 地区/線路/電柱番号 */}
        <DisplayField
          label="代表電柱"
          value={selectedText}
          variant="outlined"
          fullWidth={false}
          placeholder="電柱を選択してください"
          sx={{ width: '18rem' }}
        />

        {/* 左右分割スペーサー
        <Box sx={{ flexGrow: 1 }} />
        */}

        {/* キャンセルボタン */}
        <StandardButton
          size="large"
          variant="contained"
          onClick={handleCancel}
        >
          キャンセル
        </StandardButton>

        {/* 電柱選択ボタン */}
        <PositiveButton
          size="large"
          variant="contained"
          disabled={!selected || !selected.poleNo}
          onClick={handleSelect}
        >
          電柱選択
        </PositiveButton>
      </DialogActions>
    </Dialog>
  );
}

// プロパティ
PoleSelectDialog.propTypes = {
  initialPole:   PropTypes.object,             // 選択電柱の初期値
  isDialogOpen:  PropTypes.bool.isRequired,    // ダイアログ表示状態
  onDialogClose: PropTypes.func.isRequired,    // ダイアログ終了処理
};

// 選択電柱表示文字列
function poleDisplayText(pole) {
  return pole
      ? pole.fullText()
      : '';
}
